import React, { useState, useEffect, useCallback } from 'react';
import { inventoryAPI, productsAPI } from '../services/api';
import { getSucursalId } from '../hooks/useSucursal';
import './InventoryBotica.css';

/* ─── Helpers ─────────────────────────────────────────────── */
function diasHastaVencimiento(fechaStr) {
    if (!fechaStr) return null;
    const hoy  = new Date(); hoy.setHours(0,0,0,0);
    const venc = new Date(fechaStr); venc.setHours(0,0,0,0);
    return Math.round((venc - hoy) / (1000 * 60 * 60 * 24));
}

function claseSemaforo(dias) {
    if (dias === null) return 'verde';
    if (dias < 0)    return 'rojo';
    if (dias <= 30)  return 'rojo';
    if (dias <= 90)  return 'naranja';
    return 'verde';
}

function etiquetaDias(dias) {
    if (dias === null) return '—';
    if (dias < 0)  return `Vencido hace ${Math.abs(dias)}d`;
    if (dias === 0) return 'Vence hoy';
    return `${dias}d`;
}

/* ═══════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════════ */
const InventoryBotica = () => {
    // ── Tabs ────────────────────────────────────────────────
    const [tab, setTab]           = useState('lotes'); // 'lotes' | 'movimiento'

    // ── Lotes state ──────────────────────────────────────────
    const [lotes, setLotes]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [filtro, setFiltro]     = useState('todos');   // 'todos' | 'vencidos' | 'criticos' | 'alerta'
    const [busqueda, setBusqueda] = useState('');

    // ── Formulario movimiento ────────────────────────────────
    const [products, setProducts] = useState([]);
    const [movForm, setMovForm]   = useState({
        product_id:     '',
        movement_type:  'entrada',
        quantity:       '1',
        reason:         '',
        lote_id:        '',
    });
    const [submitMsg, setSubmitMsg] = useState('');
    const [submitting, setSubmitting] = useState(false);

    /* ── Carga de lotes ─────────────────────────────────── */
    const loadLotes = useCallback(async () => {
        try {
            setLoading(true);
            const sucursalId = getSucursalId();
            const params = {};
            if (sucursalId) params.sucursal_id = sucursalId;
            const res = await inventoryAPI.getAlertasLotes(params);
            const raw = res.data?.data?.lotes || [];
            // Ordenar: vencidos primero
            const sorted = [...raw].sort((a, b) => {
                const dA = diasHastaVencimiento(a.fecha_vencimiento) ?? 9999;
                const dB = diasHastaVencimiento(b.fecha_vencimiento) ?? 9999;
                return dA - dB;
            });
            setLotes(sorted);
        } catch (err) {
            console.error('Error al cargar lotes:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    /* ── Carga de productos (para el form de movimiento) ─── */
    useEffect(() => {
        if (tab === 'movimiento' && products.length === 0) {
            productsAPI.getAll().then(r => setProducts(r.data?.data?.products || [])).catch(() => {});
        }
    }, [tab, products.length]);

    useEffect(() => { loadLotes(); }, [loadLotes]);

    /* ── Filtrado de lotes ───────────────────────────────── */
    const lotesFiltrados = lotes.filter(l => {
        const dias  = diasHastaVencimiento(l.fecha_vencimiento);
        const clase = claseSemaforo(dias);
        const matchFiltro =
            filtro === 'todos'      ? true :
            filtro === 'vencidos'   ? (dias !== null && dias < 0) :
            filtro === 'criticos'   ? (clase === 'rojo' && dias !== null && dias >= 0) :
            filtro === 'alerta'     ? (clase === 'naranja') :
            true;
        const q = busqueda.toLowerCase();
        const matchBusqueda = !q ||
            (l.product_name || '').toLowerCase().includes(q) ||
            (l.numero_lote || '').toLowerCase().includes(q) ||
            (l.laboratorio || '').toLowerCase().includes(q);
        return matchFiltro && matchBusqueda;
    });

    /* ── Conteos para los badges ─────────────────────────── */
    const cVencidos  = lotes.filter(l => (diasHastaVencimiento(l.fecha_vencimiento) ?? 0) < 0).length;
    const cCriticos  = lotes.filter(l => { const d = diasHastaVencimiento(l.fecha_vencimiento); return d !== null && d >= 0 && d <= 30; }).length;
    const cAlerta    = lotes.filter(l => { const d = diasHastaVencimiento(l.fecha_vencimiento); return d !== null && d > 30 && d <= 90; }).length;

    /* ── Submit movimiento ───────────────────────────────── */
    const handleMovSubmit = async (e) => {
        e.preventDefault();
        if (!movForm.product_id || !movForm.quantity || !movForm.reason) {
            setSubmitMsg('⚠️ Completa todos los campos obligatorios.');
            return;
        }
        setSubmitting(true);
        setSubmitMsg('');
        try {
            await inventoryAPI.createMovement({
                product_id:    movForm.product_id,
                movement_type: movForm.movement_type,
                quantity:      movForm.quantity,
                reason:        movForm.reason,
                sucursal_id:   getSucursalId() || undefined,
            });
            setSubmitMsg('✅ Movimiento registrado exitosamente.');
            setMovForm({ product_id: '', movement_type: 'entrada', quantity: '1', reason: '', lote_id: '' });
            // Recargar lotes si fue una entrada
            if (movForm.movement_type === 'entrada') loadLotes();
        } catch (err) {
            setSubmitMsg('❌ Error al registrar el movimiento. Intenta de nuevo.');
        } finally {
            setSubmitting(false);
        }
    };

    /* ── Render ──────────────────────────────────────────── */
    return (
        <div className="inv-botica content-pane">

            {/* Header */}
            <div className="inv-botica-header">
                <div className="inv-botica-title">
                    <div className="inv-botica-title-icon">💊</div>
                    <div>
                        <h2>Control de Inventario — Farmacia</h2>
                        <p>Gestión de lotes, vencimientos y movimientos de stock</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="inv-botica-tabs">
                    <button
                        className={`inv-botica-tab ${tab === 'lotes' ? 'active' : ''}`}
                        onClick={() => setTab('lotes')}
                    >
                        🧪 Lotes y Vencimientos
                    </button>
                    <button
                        className={`inv-botica-tab ${tab === 'movimiento' ? 'active' : ''}`}
                        onClick={() => setTab('movimiento')}
                    >
                        📝 Registrar Movimiento
                    </button>
                </div>
            </div>

            {/* ── TAB: LOTES ─────────────────────────────────────── */}
            {tab === 'lotes' && (
                <>
                    {/* Badges resumen */}
                    <div className="inv-botica-alerts">
                        {cVencidos > 0 && (
                            <div className="inv-alert-badge rojo">
                                🚨 {cVencidos} lote{cVencidos !== 1 ? 's' : ''} vencido{cVencidos !== 1 ? 's' : ''}
                            </div>
                        )}
                        {cCriticos > 0 && (
                            <div className="inv-alert-badge rojo">
                                ⚠️ {cCriticos} vence en ≤30 días
                            </div>
                        )}
                        {cAlerta > 0 && (
                            <div className="inv-alert-badge naranja">
                                🕐 {cAlerta} vence en 30–90 días
                            </div>
                        )}
                        {cVencidos === 0 && cCriticos === 0 && cAlerta === 0 && (
                            <div className="inv-alert-badge verde">
                                ✅ Sin alertas de vencimiento
                            </div>
                        )}
                    </div>

                    <div className="inv-botica-panel">
                        <div className="inv-botica-panel-hdr">
                            <h3>🧪 Lotes con Alertas ({lotesFiltrados.length})</h3>
                            <button
                                className="inv-botica-tab"
                                onClick={loadLotes}
                                style={{ padding: '5px 12px', fontSize: 12 }}
                            >
                                🔄 Actualizar
                            </button>
                        </div>

                        {/* Filtros */}
                        <div className="inv-botica-filters">
                            {[
                                { key: 'todos',    label: 'Todos' },
                                { key: 'vencidos', label: '🚨 Vencidos' },
                                { key: 'criticos', label: '⚠️ Críticos (≤30d)' },
                                { key: 'alerta',   label: '🕐 Alerta (≤90d)' },
                            ].map(f => (
                                <button
                                    key={f.key}
                                    className={`inv-botica-filter-btn ${filtro === f.key ? 'active' : ''}`}
                                    onClick={() => setFiltro(f.key)}
                                >
                                    {f.label}
                                </button>
                            ))}
                            <input
                                className="inv-botica-search"
                                placeholder="Buscar medicamento, lote o laboratorio..."
                                value={busqueda}
                                onChange={e => setBusqueda(e.target.value)}
                            />
                        </div>

                        {/* Tabla */}
                        {loading ? (
                            <div className="inv-botica-loading">⏳ Cargando lotes...</div>
                        ) : lotesFiltrados.length > 0 ? (
                            <div className="inv-lotes-table-wrap">
                                <table className="inv-lotes-table">
                                    <thead>
                                        <tr>
                                            <th>Medicamento</th>
                                            <th>Lote</th>
                                            <th>Laboratorio</th>
                                            <th>Stock</th>
                                            <th>Vencimiento</th>
                                            <th>Días Restantes</th>
                                            <th>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lotesFiltrados.map((lote, i) => {
                                            const dias  = diasHastaVencimiento(lote.fecha_vencimiento);
                                            const clase = claseSemaforo(dias);
                                            const emoji = clase === 'rojo' ? '🔴' : clase === 'naranja' ? '🟡' : '🟢';
                                            const stk   = parseFloat(lote.stock_disponible ?? 0);
                                            return (
                                                <tr key={i}>
                                                    <td>
                                                        <div className="prod-name">{lote.product_name || '—'}</div>
                                                    </td>
                                                    <td>
                                                        <span className="lote-num">{lote.numero_lote || '—'}</span>
                                                    </td>
                                                    <td>
                                                        <span className="prod-lab">{lote.laboratorio || '—'}</span>
                                                    </td>
                                                    <td>
                                                        <span className={`stock-num ${stk <= 0 ? 'zero' : stk <= 5 ? 'low' : 'ok'}`}>
                                                            {stk}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {lote.fecha_vencimiento
                                                            ? new Date(lote.fecha_vencimiento).toLocaleDateString('es-PE')
                                                            : '—'}
                                                    </td>
                                                    <td>
                                                        <span className={`venc-pill ${clase}`}>
                                                            {emoji} {etiquetaDias(dias)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`venc-pill ${clase}`}>
                                                            {clase === 'rojo' && dias !== null && dias < 0 ? 'VENCIDO'  : ''}
                                                            {clase === 'rojo' && dias !== null && dias >= 0 ? 'CRÍTICO' : ''}
                                                            {clase === 'naranja' ? 'ALERTA' : ''}
                                                            {clase === 'verde'   ? 'OK'     : ''}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="inv-botica-empty">
                                <div className="icon">✅</div>
                                <p>
                                    {filtro === 'todos'
                                        ? 'Sin lotes con alertas de vencimiento.'
                                        : 'Sin lotes con este filtro.'}
                                </p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ── TAB: REGISTRAR MOVIMIENTO ───────────────────────── */}
            {tab === 'movimiento' && (
                <div className="inv-botica-panel">
                    <div className="inv-botica-panel-hdr">
                        <h3>📝 Registrar Movimiento de Stock</h3>
                    </div>

                    {submitMsg && (
                        <div className="inv-success-bar">{submitMsg}</div>
                    )}

                    <form onSubmit={handleMovSubmit} className="inv-mov-form">
                        <div className="form-group">
                            <label>Medicamento / Producto *</label>
                            <select
                                value={movForm.product_id}
                                onChange={e => setMovForm(p => ({ ...p, product_id: e.target.value }))}
                                required
                            >
                                <option value="">Seleccionar medicamento...</option>
                                {products.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}{p.laboratorio ? ` — ${p.laboratorio}` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Tipo de Movimiento *</label>
                            <select
                                value={movForm.movement_type}
                                onChange={e => setMovForm(p => ({ ...p, movement_type: e.target.value }))}
                            >
                                <option value="entrada">📥 Entrada (Compra / Recepción de lote)</option>
                                <option value="salida">📤 Salida (Ajuste / Baja / Merma)</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Cantidad *</label>
                            <input
                                type="number" min="1" step="1"
                                value={movForm.quantity}
                                onChange={e => setMovForm(p => ({ ...p, quantity: e.target.value }))}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Motivo / Observación *</label>
                            <input
                                type="text"
                                placeholder="Ej: Compra proveedor, Baja por vencimiento, Merma..."
                                value={movForm.reason}
                                onChange={e => setMovForm(p => ({ ...p, reason: e.target.value }))}
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            className="inv-mov-submit"
                            disabled={submitting}
                        >
                            {submitting ? '⏳ Registrando...' : '📝 Registrar Movimiento'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default InventoryBotica;
